import type { UserIdentity } from '@supabase/supabase-js'
import { useQuery } from '@tanstack/react-query'
import { authCallbackUrl } from './auth-callback-url.js'
import { googleLinkErrorMessage } from './error-copy.js'
import {
  clearPendingGoogleLink,
  loadPendingGoogleLink,
  savePendingGoogleLink,
} from './google-link-pending.js'
import { planGoogleSwitchCompletion } from './login-identities.js'
import { supabase } from './supabase.js'

/**
 * Google アカウントの紐付け・切り替え（Issue #118）。
 *
 * 前提: Supabase の「Manual linking」が有効であること（dev・本番とも）。
 * 無効だと linkIdentity が manual_linking_disabled で断られる。
 */

export const IDENTITIES_QUERY_KEY = ['auth-identities'] as const

export function useIdentities(options: { enabled: boolean }) {
  return useQuery({
    queryKey: IDENTITIES_QUERY_KEY,
    queryFn: async (): Promise<UserIdentity[]> => {
      const { data, error } = await supabase.auth.getUserIdentities()
      if (error) throw error
      return data.identities
    },
    enabled: options.enabled,
  })
}

/**
 * Google の画面へ移って、新しい Google アカウントを紐付ける。
 * replaceIdentityId を渡すと、戻ってきた後にその古い紐付けを外す（= 切り替え）。
 */
export async function startGoogleLink(replaceIdentityId: string | null): Promise<void> {
  savePendingGoogleLink({ replaceIdentityId })
  const { error } = await supabase.auth.linkIdentity({
    provider: 'google',
    options: { redirectTo: authCallbackUrl() },
  })
  if (error) {
    clearPendingGoogleLink()
    throw new Error(googleLinkErrorMessage(error.code ?? ''))
  }
}

export type GoogleLinkOutcome =
  | { kind: 'switched'; newEmail: string | null }
  | { kind: 'linked'; newEmail: string | null }
  | { kind: 'unchanged' }
  | { kind: 'error'; message: string }
  | { kind: 'unlink_failed' }

/**
 * Google の画面から戻ってきた後の後始末。手続き中でなければ null。
 * 切り替えなら、新しい紐付けが付いたのを確かめてから古いほうを外す。
 */
export async function completePendingGoogleLink(): Promise<GoogleLinkOutcome | null> {
  const pending = loadPendingGoogleLink()
  if (!pending) return null
  clearPendingGoogleLink()
  if (pending.error) return { kind: 'error', message: googleLinkErrorMessage(pending.error) }

  const { data, error } = await supabase.auth.getUserIdentities()
  if (error) throw error
  const plan = planGoogleSwitchCompletion(data.identities, pending.replaceIdentityId)
  if (plan.kind === 'unchanged') return plan
  if (plan.kind === 'done') return { kind: 'linked', newEmail: plan.newEmail }

  const unlinked = await supabase.auth.unlinkIdentity(plan.oldIdentity)
  if (unlinked.error) return { kind: 'unlink_failed' }
  // セッションの user.identities を新しい状態にする（設定画面の出し分けに使う）
  await supabase.auth.refreshSession()
  return { kind: 'switched', newEmail: plan.newEmail }
}
